import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bike, LogOut, RefreshCw, MapPin, Phone, Package,
  Truck, CheckCircle2, Camera, X, ChevronRight, Clock,
  User, Store, AlertCircle, PenLine, Copy, ExternalLink,
  FileText, Receipt, CreditCard, ChevronDown, Bell, BellOff,
  Share, Plus, Volume2, VolumeX, ScrollText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import QRCode from "qrcode";
import { API } from "@/lib/api";
import { useRiderPush } from "@/hooks/useRiderPush";
import { useRiderAlarm } from "@/hooks/useRiderAlarm";

// Determine what action a rider should take for a given order
const getTaskAction = (order) => {
  const { status, journey_type: jt } = order;
  switch (status) {
    case "pickup_assigned":
      return jt === "shop_to_home"
        ? { label: "Mark Picked Up", next: "at_merchant", proof: "pickup_from_customer", color: "blue", verb: "Pickup" }
        : { label: "Mark Picked Up", next: "pickup_complete", proof: "pickup_from_customer", color: "blue", verb: "Pickup" };
    case "pickup_complete":
      return jt === "c2c"
        ? { label: "Sent for Delivery", next: "delivered", proof: "delivered_to_customer", color: "emerald", verb: "Delivery" }
        : { label: "Mark at Merchant", next: "at_merchant", proof: "drop_at_merchant", color: "amber", verb: "Drop-off" };
    case "delivery_assigned":
      return { label: "Sent for Delivery", next: "delivered", proof: "delivered_to_customer", color: "emerald", verb: "Delivery" };
    default:
      return null;
  }
};

const TASK_TYPE_CONFIG = {
  pickup: { label: "Pickup", bg: "bg-blue-100 text-blue-700", icon: Truck },
  merchant_drop: { label: "Drop at Merchant", bg: "bg-amber-100 text-amber-700", icon: Store },
  awaiting_signature: { label: "Get Merchant Signature", bg: "bg-orange-100 text-orange-700", icon: PenLine },
  customer_delivery: { label: "Delivery", bg: "bg-emerald-100 text-emerald-700", icon: Package },
  c2c_delivery: { label: "C2C Delivery", bg: "bg-emerald-100 text-emerald-700", icon: Package },
  delivered: { label: "Delivered", bg: "bg-slate-100 text-slate-500", icon: CheckCircle2 },
};

const JOURNEY_LABELS = {
  home_to_shop: "Home → Shop",
  shop_to_home: "Shop → Home",
  c2c: "C2C",
};

const COLOR_CLASSES = {
  blue: "bg-[#0047FF] hover:bg-[#0033CC]",
  amber: "bg-amber-500 hover:bg-amber-600",
  emerald: "bg-emerald-600 hover:bg-emerald-700",
};

// ~11 MiB raw file stays under backend base64 + MongoDB document size cap (~15 MiB string)
const MAX_PROOF_FILE_BYTES = 11 * 1024 * 1024;
const PROOF_INPUT_ACCEPT =
  "image/*,.heic,.heif,.avif,.jfif,.bmp,.tif,.tiff,image/heic,image/heif,image/avif,image/tiff";

// ── Proof camera section ────────────────────────────────────────────────────
const ProofCapture = ({ onImageCapture, imageData, onClear }) => {
  const inputRef = useRef(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    setPreviewFailed(false);
  }, [imageData]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_PROOF_FILE_BYTES) {
      toast.error("Photo is too large. Try a smaller image or lower camera resolution.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => onImageCapture(ev.target.result);
    reader.onerror = () => {
      toast.error("Could not read this file. Try another format.");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  if (imageData) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
        {previewFailed ? (
          <div className="w-full h-40 flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-600 text-xs px-4 text-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <span>File attached (preview not supported in browser)</span>
          </div>
        ) : (
          <img
            src={imageData}
            alt="proof"
            className="w-full h-40 object-cover"
            onError={() => setPreviewFailed(true)}
          />
        )}
        <button
          onClick={onClear}
          className="absolute top-2 right-2 bg-black/60 rounded-full p-1 text-white"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Photo added
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => inputRef.current?.click()}
      className="w-full h-20 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center gap-1.5 text-slate-500 active:bg-slate-100 transition-colors"
    >
      <Camera className="w-5 h-5" />
      <span className="text-xs font-medium">Add Proof Photo (optional)</span>
      <input
        ref={inputRef}
        type="file"
        accept={PROOF_INPUT_ACCEPT}
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />
    </button>
  );
};

// ── Format INR ──────────────────────────────────────────────────────────────
const fmtINR = (amt) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amt || 0);

// ── Invoice section shown inside TaskCard ────────────────────────────────────
const InvoiceSection = ({ customerInvoice, vendorInvoice }) => {
  const [custOpen, setCustOpen] = useState(false);
  const [vendOpen, setVendOpen] = useState(false);
  const [custQr, setCustQr] = useState("");
  const [vendQr, setVendQr] = useState("");

  useEffect(() => {
    if (customerInvoice) {
      const link = customerInvoice.payment_link || "https://razorpay.me/@pokus";
      QRCode.toDataURL(link, {
        width: 100, margin: 1,
        color: { dark: "#0047FF", light: "#ffffff" },
      }).then(setCustQr).catch(console.error);
    }
    if (vendorInvoice?.vendor_upi_id) {
      QRCode.toDataURL(
        `upi://pay?pa=${vendorInvoice.vendor_upi_id}&pn=${encodeURIComponent(vendorInvoice.vendor_name || "")}`,
        { width: 100, margin: 1, color: { dark: "#0f172a", light: "#ffffff" } }
      ).then(setVendQr).catch(console.error);
    }
  }, [customerInvoice, vendorInvoice]);

  if (!customerInvoice && !vendorInvoice) return null;

  return (
    <div className="space-y-2.5">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Invoices</p>

      {/* ── Customer Invoice ── */}
      {customerInvoice && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-3 py-2.5"
            onClick={() => setCustOpen((v) => !v)}
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-[10px] text-blue-500 uppercase tracking-wide font-semibold">Customer Invoice</p>
                <p className="text-xs font-mono font-bold text-blue-800">{customerInvoice.invoice_number}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono font-bold text-blue-700">{fmtINR(customerInvoice.total_payable)}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-blue-400 transition-transform ${custOpen ? "rotate-180" : ""}`} />
            </div>
          </button>

          {custOpen && (
            <div className="px-3 pb-3 space-y-2.5">
              {/* Bill To */}
              <div className="flex items-center gap-2 text-xs text-blue-700">
                <User className="w-3 h-3 shrink-0" />
                <span className="font-medium">{customerInvoice.customer_name}</span>
                {customerInvoice.phone && <span className="text-blue-500">· {customerInvoice.phone}</span>}
              </div>

              {/* Line items */}
              <div className="rounded-lg bg-white border border-blue-100 overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-blue-100 bg-blue-50/50">
                      <th className="text-left py-1.5 px-2 text-blue-500 font-semibold">Service</th>
                      <th className="text-center py-1.5 px-1 text-blue-500 font-semibold">Qty</th>
                      <th className="text-right py-1.5 px-2 text-blue-500 font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(customerInvoice.line_items || []).map((item, i) => (
                      <tr key={i} className="border-b border-blue-50 last:border-0">
                        <td className="py-1.5 px-2 text-slate-700">
                          {(item.service_type === "Other Services" || item.service_type === "Commerce") && item.custom_service
                            ? item.custom_service
                            : item.service_type}
                        </td>
                        <td className="py-1.5 px-1 text-center font-mono text-slate-600">{item.quantity}</td>
                        <td className="py-1.5 px-2 text-right font-mono font-medium text-slate-800">{fmtINR(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="space-y-1 text-xs">
                {customerInvoice.discount > 0 && (
                  <div className="flex justify-between px-1">
                    <span className="text-slate-500">Discount</span>
                    <span className="font-mono text-red-500">-{fmtINR(customerInvoice.discount)}</span>
                  </div>
                )}
                {customerInvoice.delivery_fee > 0 && (
                  <div className="flex justify-between px-1">
                    <span className="text-slate-500">Delivery Fee</span>
                    <span className="font-mono text-slate-700">+{fmtINR(customerInvoice.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between px-1 pt-1.5 border-t border-blue-200">
                  <span className="font-bold text-slate-800">Total Payable</span>
                  <span className="font-mono font-bold text-blue-700">{fmtINR(customerInvoice.total_payable)}</span>
                </div>
              </div>

              {/* Payment status & link */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  customerInvoice.payment_status === "paid"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                }`}>
                  <CreditCard className="w-2.5 h-2.5" />
                  {customerInvoice.payment_status === "paid" ? "Paid" : "Unpaid"}
                </span>
                {customerInvoice.payment_link && (
                  <a
                    href={customerInvoice.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-blue-600 font-medium flex items-center gap-1"
                  >
                    <ExternalLink className="w-2.5 h-2.5" /> Payment Link
                  </a>
                )}
              </div>

              {/* QR Code - Scan to Pay */}
              {custQr && customerInvoice.payment_status !== "paid" && (
                <div className="flex flex-col items-center gap-1.5 pt-1">
                  <img src={custQr} alt="Scan to Pay" className="w-20 h-20 rounded-lg border border-blue-100" />
                  <p className="text-[10px] font-semibold text-blue-500">Scan to Pay</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Vendor Invoice ── */}
      {vendorInvoice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-3 py-2.5"
            onClick={() => setVendOpen((v) => !v)}
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Receipt className="w-3.5 h-3.5 text-emerald-600" />
              </div>
              <div className="text-left">
                <p className="text-[10px] text-emerald-500 uppercase tracking-wide font-semibold">Vendor Invoice</p>
                <p className="text-xs font-mono font-bold text-emerald-800">{vendorInvoice.invoice_number}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono font-bold text-emerald-700">{fmtINR(vendorInvoice.vendor_total || vendorInvoice.total_payable)}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-emerald-400 transition-transform ${vendOpen ? "rotate-180" : ""}`} />
            </div>
          </button>

          {vendOpen && (
            <div className="px-3 pb-3 space-y-2.5">
              {/* Vendor info */}
              <div className="flex items-center gap-2 text-xs text-emerald-700">
                <Store className="w-3 h-3 shrink-0" />
                <span className="font-medium">{vendorInvoice.vendor_name}</span>
                {vendorInvoice.vendor_contact && <span className="text-emerald-500">· {vendorInvoice.vendor_contact}</span>}
              </div>

              {/* Line items */}
              <div className="rounded-lg bg-white border border-emerald-100 overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-emerald-100 bg-emerald-50/50">
                      <th className="text-left py-1.5 px-2 text-emerald-500 font-semibold">Service</th>
                      <th className="text-center py-1.5 px-1 text-emerald-500 font-semibold">Qty</th>
                      <th className="text-right py-1.5 px-1 text-emerald-500 font-semibold">CP</th>
                      <th className="text-right py-1.5 px-1 text-emerald-500 font-semibold">SP</th>
                      <th className="text-right py-1.5 px-2 text-emerald-500 font-semibold">Vendor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(vendorInvoice.line_items || []).map((item, i) => {
                      const cp = item.cost_price || item.price_per_unit || 0;
                      const sp = item.selling_price || item.price_per_unit || 0;
                      const vendorAmt = item.quantity * cp;
                      return (
                        <tr key={i} className="border-b border-emerald-50 last:border-0">
                          <td className="py-1.5 px-2 text-slate-700">{item.service_type}</td>
                          <td className="py-1.5 px-1 text-center font-mono text-slate-600">{item.quantity}</td>
                          <td className="py-1.5 px-1 text-right font-mono text-slate-600">{fmtINR(cp)}</td>
                          <td className="py-1.5 px-1 text-right font-mono text-slate-600">{fmtINR(sp)}</td>
                          <td className="py-1.5 px-2 text-right font-mono font-medium text-slate-800">{fmtINR(vendorAmt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="space-y-1 text-xs">
                <div className="flex justify-between px-1">
                  <span className="text-slate-500">Vendor Subtotal</span>
                  <span className="font-mono text-slate-700">{fmtINR(vendorInvoice.vendor_subtotal || vendorInvoice.subtotal)}</span>
                </div>
                <div className="flex justify-between px-1">
                  <span className="text-emerald-600">Pokus Fees</span>
                  <span className="font-mono text-emerald-600">{fmtINR(vendorInvoice.pokus_fees_total || 0)}</span>
                </div>
                {vendorInvoice.delivery_fee > 0 && (
                  <div className="flex justify-between px-1">
                    <span className="text-slate-500">Delivery Fee</span>
                    <span className="font-mono text-slate-700">+{fmtINR(vendorInvoice.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between px-1 pt-1.5 border-t border-emerald-200">
                  <span className="font-bold text-slate-800">Vendor Total</span>
                  <span className="font-mono font-bold text-emerald-700">{fmtINR(vendorInvoice.vendor_total || vendorInvoice.total_payable)}</span>
                </div>
                <div className="flex justify-between px-1">
                  <span className="font-bold text-slate-800">Customer Total</span>
                  <span className="font-mono font-bold text-blue-700">{fmtINR(vendorInvoice.customer_total || vendorInvoice.total_payable)}</span>
                </div>
              </div>

              {/* Signature status */}
              <div className="flex items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  vendorInvoice.signature_status === "Signed"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                }`}>
                  {vendorInvoice.signature_status === "Signed"
                    ? <><CheckCircle2 className="w-2.5 h-2.5" /> Signed</>
                    : <><Clock className="w-2.5 h-2.5" /> Signature Pending</>
                  }
                </span>
              </div>

              {/* Vendor UPI QR Code */}
              {vendQr && (
                <div className="flex flex-col items-center gap-1.5 pt-1">
                  <img src={vendQr} alt="Vendor UPI QR" className="w-20 h-20 rounded-lg border border-emerald-100" />
                  <p className="text-[10px] font-semibold text-emerald-500">Scan to Pay Vendor</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Single task card ─────────────────────────────────────────────────────────
const TaskCard = ({ task, riderId, riderName, onRefresh }) => {
  // Backend returns flat object: {...orderFields, task_type, assignment_type, customer_invoice, vendor_invoice}
  const { task_type, assignment_type, customer_invoice, vendor_invoice, ...order } = task;
  const action = getTaskAction(order);
  const typeConfig = TASK_TYPE_CONFIG[task_type] || TASK_TYPE_CONFIG.pickup;
  const TypeIcon = typeConfig.icon;

  const [proofImage, setProofImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Pickup details form — only for pickup_assigned tasks
  const isPickupTask = order.status === "pickup_assigned";
  // Merchant drop form — shown at pickup_complete for H2S (rider drops off at merchant)
  const isMerchantTask = task_type === "merchant_drop";
  const merchantDetailsSubmitted = !!(order.merchant_reached_time || order.items_submitted);

  const nowLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const resolvePickupAddress = () => {
    if (order.pickup_address) return order.pickup_address;
    // S2H: pickup is from the shop, so fall back to merchant address
    if (order.journey_type === "shop_to_home" && order.merchant_address) return order.merchant_address;
    return "";
  };
  const resolveShopLocation = () => order.merchant_name || order.merchant_address || "";
  const resolveShopPickupLocation = () => order.shop_location || order.merchant_name || order.merchant_address || "";
  const resolveDeliveryAddress = () => {
    if (order.delivery_address) return order.delivery_address;
    // H2S: deliver back to the customer's original pickup address
    if (order.journey_type === "home_to_shop" && order.pickup_address) return order.pickup_address;
    return "";
  };

  const [pickupForm, setPickupForm] = useState({
    pickup_time: order.pickup_time || nowLocal(),
    pickup_address: resolvePickupAddress(),
    items_collected: order.items_collected || "",
  });
  const [merchantForm, setMerchantForm] = useState({
    merchant_reached_time: nowLocal(),
    shop_location: resolveShopLocation(),
    items_submitted: order.items_submitted || "",
  });
  const [submittingMerchant, setSubmittingMerchant] = useState(false);

  // Delivery task — H2S has two phases (shop pickup then customer delivery), S2H is single phase
  const isDeliveryTask = task_type === "customer_delivery";
  // C2C delivery task — single phase: deliver directly to customer (no shop pickup step)
  const isC2CDelivery = task_type === "c2c_delivery";
  // H2S: rider must first pick up from shop before delivering to customer
  const isH2SDelivery = isDeliveryTask && order.journey_type === "home_to_shop";
  const shopPickupDone = !isH2SDelivery || !!(order.shop_pickup_time);
  const [shopPickupProof, setShopPickupProof] = useState(null);
  const [deliveryProof, setDeliveryProof] = useState(null);
  const [shopPickupForm, setShopPickupForm] = useState({
    shop_pickup_time: nowLocal(),
    shop_pickup_location: resolveShopPickupLocation(),
    shop_pickup_items: order.shop_pickup_items || order.items_submitted || "",
  });
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_time: order.delivery_time || nowLocal(),
    delivery_address: resolveDeliveryAddress(),
    delivered_items: order.shop_pickup_items || order.items_submitted || "",
  });

  // Sync forms when order data changes (e.g. RM fills address after rider opened dashboard)
  useEffect(() => {
    setPickupForm(prev => ({
      ...prev,
      pickup_address: prev.pickup_address || resolvePickupAddress(),
      items_collected: prev.items_collected || order.items_collected || "",
    }));
  }, [order.pickup_address, order.merchant_address, order.journey_type, order.items_collected]);

  useEffect(() => {
    setMerchantForm(prev => ({
      ...prev,
      shop_location: prev.shop_location || resolveShopLocation(),
    }));
  }, [order.merchant_name, order.merchant_address]);

  useEffect(() => {
    setShopPickupForm(prev => ({
      ...prev,
      shop_pickup_location: prev.shop_pickup_location || resolveShopPickupLocation(),
      shop_pickup_items: prev.shop_pickup_items || order.shop_pickup_items || order.items_submitted || "",
    }));
  }, [order.shop_location, order.merchant_name, order.merchant_address, order.shop_pickup_items, order.items_submitted]);

  useEffect(() => {
    setDeliveryForm(prev => ({
      ...prev,
      delivery_address: prev.delivery_address || resolveDeliveryAddress(),
      delivered_items: prev.delivered_items || order.shop_pickup_items || order.items_submitted || "",
    }));
  }, [order.delivery_address, order.pickup_address, order.journey_type, order.shop_pickup_items, order.items_submitted]);
  const [submittingShopPickup, setSubmittingShopPickup] = useState(false);

  const contactName = order.customer_name;
  const contactPhone = order.customer_phone;

  const jt = order.journey_type || "home_to_shop";
  const h2sSwap = jt === "home_to_shop" && (isDeliveryTask || isC2CDelivery);
  const pickupAddr = h2sSwap ? (order.merchant_address || order.delivery_address || "") : (order.pickup_address || "");
  const dropAddr = h2sSwap ? (order.pickup_address || "") : (order.delivery_address || "");
  const isMapUrl = (s) => /^https?:\/\//i.test(s);

  const locationName = order.merchant_name;
  const locationAddress = order.merchant_address;
  const mapsLink = order.merchant_maps_link;

  const handleAction = async () => {
    if (!action) return;

    if ((isDeliveryTask && shopPickupDone) || isC2CDelivery) {
      if (!deliveryForm.delivered_items.trim()) { toast.error("Please enter items delivered"); return; }
    }

    setSubmitting(true);
    try {
      // Save pickup details first if this is a pickup task
      if (isPickupTask) {
        const pickupRes = await fetch(`${API}/orders/${order.id}/pickup-details`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pickupForm),
        });
        if (!pickupRes.ok) {
          let msg = "Failed to save pickup details";
          try { const d = await pickupRes.json(); msg = d.detail || msg; } catch {}
          throw new Error(msg);
        }
      }

      // Save delivery details if this is the final delivery action
      if ((isDeliveryTask && shopPickupDone) || isC2CDelivery) {
        const deliveryRes = await fetch(`${API}/orders/${order.id}/delivery-details`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(deliveryForm),
        });
        if (!deliveryRes.ok) {
          let msg = "Failed to save delivery details";
          try { const d = await deliveryRes.json(); msg = d.detail || msg; } catch {}
          throw new Error(msg);
        }
      }

      // Upload proof
      const activeProof = (isDeliveryTask && shopPickupDone) || isC2CDelivery ? deliveryProof : proofImage;
      if (activeProof) {
        const proofRes = await fetch(`${API}/orders/${order.id}/proof`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            proof_type: action.proof,
            image_data: activeProof,
            uploaded_by: riderId,
            uploaded_by_name: riderName || "Rider",
            note: "",
          }),
        });
        if (!proofRes.ok) {
          let msg = "Failed to upload proof";
          try { const d = await proofRes.json(); msg = d.detail || msg; } catch {}
          throw new Error(msg);
        }
      }

      // Advance status
      const statusRes = await fetch(`${API}/orders/${order.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: action.next }),
      });
      if (!statusRes.ok) {
        let detail = "";
        try {
          const text = await statusRes.text();
          try { const d = JSON.parse(text); detail = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail); }
          catch { detail = text.slice(0, 300); }
        } catch {}
        // If the order status was already advanced (e.g. by RM), delivery details are still saved — treat as success
        if (detail && detail.startsWith("Cannot transition")) {
          toast.success(`Order ${order.order_number} details saved.`);
          setProofImage(null);
          onRefresh();
          return;
        }
        throw new Error(detail || `Failed to update status (HTTP ${statusRes.status})`);
      }

      toast.success(`Order ${order.order_number} updated!`);
      setProofImage(null);
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Action failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMerchantSubmit = async () => {
    setSubmittingMerchant(true);
    try {
      // Upload proof photo if provided
      if (proofImage) {
        const proofRes = await fetch(`${API}/orders/${order.id}/proof`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            proof_type: "drop_at_merchant",
            image_data: proofImage,
            uploaded_by: riderId,
            uploaded_by_name: riderName || "Rider",
            note: "",
          }),
        });
        if (!proofRes.ok) throw new Error("Failed to upload photo");
      }
      const res = await fetch(`${API}/orders/${order.id}/merchant-details`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(merchantForm),
      });
      if (!res.ok) { let msg = "Request failed"; try { const d = await res.json(); msg = d.detail || msg; } catch {} throw new Error(msg); }
      // For merchant_drop tasks, also advance status to at_merchant
      if (task_type === "merchant_drop" && action) {
        const statusRes = await fetch(`${API}/orders/${order.id}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: action.next }),
        });
        if (!statusRes.ok) {
          let detail = "";
          try { const text = await statusRes.text(); try { const d = JSON.parse(text); detail = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail); } catch { detail = text.slice(0, 300); } } catch {}
          if (detail && detail.startsWith("Cannot transition")) {
            toast.success("Items dropped at merchant!");
            setProofImage(null);
            onRefresh();
            return;
          }
          throw new Error(detail || "Failed to update status");
        }
      }
      toast.success("Items dropped at merchant!");
      setProofImage(null);
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Failed to submit details. Try again.");
    } finally {
      setSubmittingMerchant(false);
    }
  };

  const handleShopPickupSubmit = async () => {
    setSubmittingShopPickup(true);
    try {
      if (shopPickupProof) {
        const proofRes = await fetch(`${API}/orders/${order.id}/proof`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            proof_type: "pickup_from_merchant",
            image_data: shopPickupProof,
            uploaded_by: riderId,
            uploaded_by_name: riderName || "Rider",
            note: "Picked up from shop",
          }),
        });
        if (!proofRes.ok) throw new Error("Failed to upload photo");
      }
      const res = await fetch(`${API}/orders/${order.id}/shop-pickup-details`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(shopPickupForm),
      });
      if (!res.ok) { let msg = "Request failed"; try { const d = await res.json(); msg = d.detail || msg; } catch {} throw new Error(msg); }
      toast.success("Shop pickup recorded! Proceed to customer delivery.");
      setShopPickupProof(null);
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Failed to submit. Try again.");
    } finally {
      setSubmittingShopPickup(false);
    }
  };

  // H2S Phase 2: save delivery details only (RM closes the order)
  const handleDeliverySubmit = async () => {
    if (!deliveryForm.delivered_items.trim()) { toast.error("Please enter items delivered"); return; }

    setSubmitting(true);
    try {
      const deliveryRes = await fetch(`${API}/orders/${order.id}/delivery-details`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(deliveryForm),
      });
      if (!deliveryRes.ok) {
        let msg = "Failed to save delivery details";
        try { const d = await deliveryRes.json(); msg = d.detail || msg; } catch {}
        throw new Error(msg);
      }
      if (deliveryProof) {
        const proofRes = await fetch(`${API}/orders/${order.id}/proof`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            proof_type: "delivered_to_customer",
            image_data: deliveryProof,
            uploaded_by: riderId,
            uploaded_by_name: riderName || "Rider",
            note: "",
          }),
        });
        if (!proofRes.ok) throw new Error("Failed to upload proof photo");
      }
      toast.success("Delivery details saved! RM will close the order.");
      setDeliveryProof(null);
      onRefresh();
    } catch (e) {
      toast.error(e.message || "Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Signing task state
  const isSigningTask = task_type === "awaiting_signature";
  const isSigned = order.order_signature_status === "signed";
  const signingUrl = order.signing_token ? `${window.location.origin}/order-sign/${order.signing_token}` : null;

  // awaiting_signature is "completed" once merchant has signed
  // customer_delivery: completed when delivery details are filled (or order closed)
  const isCompleted = task_type === "delivered" ||
    (task_type === "awaiting_signature" && isSigned) ||
    (task_type === "customer_delivery" && (
      order.status === "closed" ||
      (order.status === "delivered" && !!order.delivered_items)
    )) ||
    (task_type === "c2c_delivery" && order.status === "delivered");

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${isCompleted ? "opacity-60" : "border-slate-200"}`}>
      {/* Card header */}
      <button
        className="w-full flex items-center justify-between p-4"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${typeConfig.bg}`}>
            <TypeIcon className="w-4.5 h-4.5" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-900 uppercase tracking-wide">{typeConfig.label}</span>
              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-mono">{order.order_number}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">{order.customer_name}</p>
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Info grid */}
          <div className="rounded-xl bg-slate-50 divide-y divide-slate-100">
            <div className="flex items-center gap-3 px-3 py-2.5">
              <User className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Customer</p>
                <p className="text-sm font-medium text-slate-800">{contactName}</p>
              </div>
            </div>
            {contactPhone && (
              <a href={`tel:${contactPhone}`} className="flex items-center gap-3 px-3 py-2.5 active:bg-slate-100">
                <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">Call Customer</p>
                  <p className="text-sm font-medium text-blue-600">{contactPhone}</p>
                </div>
              </a>
            )}
            {locationName && (
              <div className="flex items-center gap-3 px-3 py-2.5">
                <Store className="w-4 h-4 text-slate-400 shrink-0" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">Merchant</p>
                  <p className="text-sm font-medium text-slate-800">{locationName}</p>
                </div>
              </div>
            )}
            {locationAddress && (
              <div className="flex items-start gap-3 px-3 py-2.5">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">Merchant Address</p>
                  <p className="text-sm text-slate-700">{locationAddress}</p>
                </div>
              </div>
            )}
            {pickupAddr && (
              <div className="flex items-start gap-3 px-3 py-2.5">
                <MapPin className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">Pickup Address</p>
                  {isMapUrl(pickupAddr) ? (
                    <a href={pickupAddr} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline break-all">
                      {pickupAddr}
                    </a>
                  ) : (
                    <p className="text-sm text-slate-700">{pickupAddr}</p>
                  )}
                </div>
              </div>
            )}
            {dropAddr && (
              <div className="flex items-start gap-3 px-3 py-2.5">
                <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">Drop Address</p>
                  {isMapUrl(dropAddr) ? (
                    <a href={dropAddr} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline break-all">
                      {dropAddr}
                    </a>
                  ) : (
                    <p className="text-sm text-slate-700">{dropAddr}</p>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 px-3 py-2.5">
              <Clock className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Service · Journey</p>
                <p className="text-sm text-slate-700">{order.service_type} · {JOURNEY_LABELS[order.journey_type] || order.journey_type}</p>
              </div>
            </div>
            {(order.rm_names || []).length > 0 && (
              <div className="px-3 py-2.5 space-y-1.5">
                {order.rm_names.map((name, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-indigo-400 shrink-0" />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide">RM</p>
                        <p className="text-sm font-medium text-slate-800">{name}</p>
                      </div>
                    </div>
                    {(order.rm_phones || [])[i] && (
                      <a href={`tel:${order.rm_phones[i]}`} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 active:bg-indigo-100">
                        <Phone className="w-3.5 h-3.5 text-indigo-500" />
                        <span className="text-xs font-medium text-indigo-600">{order.rm_phones[i]}</span>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Maps buttons */}
          <div className="space-y-2">
            {pickupAddr && isMapUrl(pickupAddr) && (
              <a
                href={pickupAddr}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-blue-300 text-sm font-medium text-blue-700 bg-blue-50 active:bg-blue-100"
              >
                <MapPin className="w-4 h-4 text-blue-500" />
                Navigate to Pickup
              </a>
            )}
            {dropAddr && isMapUrl(dropAddr) && (
              <a
                href={dropAddr}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-emerald-300 text-sm font-medium text-emerald-700 bg-emerald-50 active:bg-emerald-100"
              >
                <MapPin className="w-4 h-4 text-emerald-500" />
                Navigate to Drop
              </a>
            )}
            {mapsLink && (
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-slate-300 text-sm font-medium text-slate-700 bg-white active:bg-slate-50"
              >
                <MapPin className="w-4 h-4 text-red-500" />
                Navigate to Merchant
              </a>
            )}
          </div>

          {/* Invoice details for rider */}
          <InvoiceSection customerInvoice={customer_invoice} vendorInvoice={vendor_invoice} />

          {/* Merchant drop-off form — shown at pickup_complete for H2S before submission */}
          {isMerchantTask && !merchantDetailsSubmitted && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1.5 mb-2">
                <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Drop Off at Merchant</p>
                <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Arrive at the merchant location above</li>
                  <li>Hand over all customer items to merchant staff</li>
                  <li>Fill in details below and confirm the drop-off</li>
                </ol>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 space-y-3">
                <p className="text-xs font-bold text-orange-800 uppercase tracking-wide">Shop Arrival Details</p>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Reached Shop Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-9 text-sm bg-white"
                    value={merchantForm.merchant_reached_time}
                    onChange={(e) => setMerchantForm(f => ({ ...f, merchant_reached_time: e.target.value }))}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Shop Name / Location</Label>
                  <Input
                    className="h-9 text-sm bg-white"
                    placeholder="e.g. IronMan Dry Cleaners, MG Road"
                    value={merchantForm.shop_location}
                    onChange={(e) => setMerchantForm(f => ({ ...f, shop_location: e.target.value }))}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Items Submitted</Label>
                  <Textarea
                    className="text-sm bg-white resize-none"
                    rows={2}
                    placeholder="e.g. 2 shirts, 1 suit jacket, 3 trousers"
                    value={merchantForm.items_submitted}
                    onChange={(e) => setMerchantForm(f => ({ ...f, items_submitted: e.target.value }))}
                  />
                </div>
              </div>

              <ProofCapture
                imageData={proofImage}
                onImageCapture={setProofImage}
                onClear={() => setProofImage(null)}
              />
              <Button
                onClick={handleMerchantSubmit}
                disabled={submittingMerchant}
                className="w-full h-13 text-base font-semibold rounded-xl text-white bg-orange-500 hover:bg-orange-600"
              >
                {submittingMerchant ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Confirm Drop-Off at Merchant
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* Merchant drop completed — details submitted, waiting for RM */}
          {isMerchantTask && merchantDetailsSubmitted && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-emerald-600 font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Items dropped at merchant — RM will generate invoice
            </div>
          )}

          {/* Signature task — show signing link for rider to hand to merchant */}
          {isSigningTask && !isSigned && signingUrl && (
            <div className="space-y-3">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 space-y-2">
                <p className="text-xs font-bold text-orange-800 uppercase tracking-wide">Get Merchant Signature</p>
                <p className="text-xs text-slate-600">Open this link on the merchant's device and have them sign the invoice.</p>
                <div className="flex gap-2 items-center">
                  <code className="text-[10px] bg-white border rounded px-2 py-1 flex-1 truncate">{signingUrl}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(signingUrl); toast.success("Link copied!"); }}
                    className="p-1.5 rounded-lg bg-white border text-slate-500 active:bg-slate-50"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <a
                  href={signingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold active:bg-orange-700"
                >
                  <ExternalLink className="w-4 h-4" /> Open Signing Page
                </a>
              </div>
            </div>
          )}

          {/* Signing done */}
          {isSigningTask && isSigned && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-emerald-600 font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Merchant has signed — RM will process payout
            </div>
          )}

          {/* ── Delivery task — Phase 1: Shop Pickup (H2S only) ── */}
          {isDeliveryTask && !shopPickupDone && (
            <div className="space-y-3">
              <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 space-y-1.5 mb-2">
                <p className="text-xs font-bold text-cyan-800 uppercase tracking-wide">Pick Up from Shop</p>
                <ol className="text-xs text-cyan-700 space-y-1 list-decimal list-inside">
                  <li>Go to the merchant location above</li>
                  <li>Get the merchant to sign the invoice (link below)</li>
                  <li>Collect the processed items from merchant staff</li>
                  <li>Fill in details below and confirm pickup</li>
                </ol>
              </div>

              {/* H2S shop-pickup: get merchant signature on the invoice */}
              {isH2SDelivery && signingUrl && !isSigned && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <PenLine className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-orange-800 uppercase tracking-wide">Get Merchant Signature</p>
                      <p className="text-xs text-orange-700 mt-0.5">Open the signing page on the merchant's device and have them sign the invoice before collecting items.</p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <code className="text-[10px] bg-white border rounded px-2 py-1 flex-1 truncate">{signingUrl}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(signingUrl); toast.success("Link copied!"); }}
                      className="p-1.5 rounded-lg bg-white border text-slate-500 active:bg-slate-50"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <a
                    href={signingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold active:bg-orange-700"
                  >
                    <ExternalLink className="w-4 h-4" /> Open Signing Page
                  </a>
                </div>
              )}
              {isH2SDelivery && isSigned && (
                <div className="flex items-center justify-center gap-2 py-2 text-sm text-emerald-600 font-medium bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckCircle2 className="w-4 h-4" />
                  Merchant signed the invoice
                </div>
              )}
              <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 space-y-3">
                <p className="text-xs font-bold text-cyan-800 uppercase tracking-wide">Shop Pickup Details</p>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Picked Up from Shop Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-9 text-sm bg-white"
                    value={shopPickupForm.shop_pickup_time}
                    onChange={(e) => setShopPickupForm(f => ({ ...f, shop_pickup_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Shop Name / Location</Label>
                  <Input
                    className="h-9 text-sm bg-white"
                    placeholder="e.g. IronMan Dry Cleaners, MG Road"
                    value={shopPickupForm.shop_pickup_location}
                    onChange={(e) => setShopPickupForm(f => ({ ...f, shop_pickup_location: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Items Collected from Shop</Label>
                  <Textarea
                    className="text-sm bg-white resize-none"
                    rows={2}
                    placeholder="e.g. 2 shirts, 1 suit jacket, 3 trousers"
                    value={shopPickupForm.shop_pickup_items}
                    onChange={(e) => setShopPickupForm(f => ({ ...f, shop_pickup_items: e.target.value }))}
                  />
                </div>
              </div>
              <ProofCapture
                imageData={shopPickupProof}
                onImageCapture={setShopPickupProof}
                onClear={() => setShopPickupProof(null)}
              />
              <Button
                onClick={handleShopPickupSubmit}
                disabled={submittingShopPickup}
                className="w-full h-13 text-base font-semibold rounded-xl text-white bg-cyan-600 hover:bg-cyan-700"
              >
                {submittingShopPickup ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Confirm Shop Pickup
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* ── Non-H2S delivery: single-phase at delivery_assigned (S2H etc.) ── */}
          {isDeliveryTask && !isH2SDelivery && !isCompleted && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1.5">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Drop Off to Customer</p>
                <ol className="text-xs text-emerald-700 space-y-1 list-decimal list-inside">
                  <li>Head to the customer's delivery address</li>
                  <li>Call customer to confirm arrival</li>
                  <li>Hand over items and fill in details below</li>
                </ol>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-3">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Delivery Details</p>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Delivery Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-9 text-sm bg-white"
                    value={deliveryForm.delivery_time}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivery_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Delivery Address</Label>
                  <Input
                    className="h-9 text-sm bg-white"
                    placeholder="Customer's address"
                    value={deliveryForm.delivery_address}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivery_address: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Items Delivered</Label>
                  <Textarea
                    className="text-sm bg-white resize-none"
                    rows={2}
                    placeholder="e.g. 2 shirts, 1 suit jacket, 3 trousers"
                    value={deliveryForm.delivered_items}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivered_items: e.target.value }))}
                  />
                </div>
              </div>
              <ProofCapture
                imageData={deliveryProof}
                onImageCapture={setDeliveryProof}
                onClear={() => setDeliveryProof(null)}
              />
              <Button
                onClick={handleAction}
                disabled={submitting}
                className="w-full h-13 text-base font-semibold rounded-xl text-white bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Completing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Sent for Delivery
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* ── H2S Phase 2: Customer delivery at delivered status (save only, RM closes) ── */}
          {isH2SDelivery && order.status === "delivered" && !isCompleted && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1.5">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Drop Off to Customer</p>
                <ol className="text-xs text-emerald-700 space-y-1 list-decimal list-inside">
                  <li>Head to the customer's delivery address</li>
                  <li>Call customer to confirm arrival</li>
                  <li>Hand over items and fill in details below</li>
                </ol>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-3">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Delivery Details</p>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Delivery Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-9 text-sm bg-white"
                    value={deliveryForm.delivery_time}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivery_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Delivery Address</Label>
                  <Input
                    className="h-9 text-sm bg-white"
                    placeholder="Customer's address"
                    value={deliveryForm.delivery_address}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivery_address: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Items Delivered</Label>
                  <Textarea
                    className="text-sm bg-white resize-none"
                    rows={2}
                    placeholder="e.g. 2 shirts, 1 suit jacket, 3 trousers"
                    value={deliveryForm.delivered_items}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivered_items: e.target.value }))}
                  />
                </div>
              </div>
              <ProofCapture
                imageData={deliveryProof}
                onImageCapture={setDeliveryProof}
                onClear={() => setDeliveryProof(null)}
              />
              <Button
                onClick={handleDeliverySubmit}
                disabled={submitting}
                className="w-full h-13 text-base font-semibold rounded-xl text-white bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Submit Delivery Details
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* ── C2C Delivery task — single phase: deliver to customer ── */}
          {isC2CDelivery && !isCompleted && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-3">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Delivery Details</p>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Delivery Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-9 text-sm bg-white"
                    value={deliveryForm.delivery_time}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivery_time: e.target.value }))}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Delivery Address</Label>
                  <Input
                    className="h-9 text-sm bg-white"
                    placeholder="Customer's delivery address"
                    value={deliveryForm.delivery_address}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivery_address: e.target.value }))}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Items Delivered</Label>
                  <Textarea
                    className="text-sm bg-white resize-none"
                    rows={2}
                    placeholder="e.g. 2 shirts, 1 suit jacket"
                    value={deliveryForm.delivered_items}
                    onChange={(e) => setDeliveryForm(f => ({ ...f, delivered_items: e.target.value }))}
                  />
                </div>
              </div>

              <ProofCapture
                imageData={deliveryProof}
                onImageCapture={setDeliveryProof}
                onClear={() => setDeliveryProof(null)}
              />
              <Button
                onClick={handleAction}
                disabled={submitting}
                className="w-full h-13 text-base font-semibold rounded-xl text-white bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Completing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Sent for Delivery
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* Action area for non-merchant, non-delivery tasks */}
          {action && !isCompleted && !isMerchantTask && !isSigningTask && !isDeliveryTask && !isC2CDelivery && (
            <div className="space-y-3">

              {/* Pickup details form — only shown when rider is doing a pickup */}
              {isPickupTask && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-3">
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Pickup Details</p>

                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Pickup Time</Label>
                    <Input
                      type="datetime-local"
                      className="h-9 text-sm bg-white"
                      value={pickupForm.pickup_time}
                      onChange={(e) => setPickupForm(f => ({ ...f, pickup_time: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Pickup Address</Label>
                    <Input
                      className="h-9 text-sm bg-white"
                      placeholder="Pickup address or Google Maps link"
                      value={pickupForm.pickup_address}
                      onChange={(e) => setPickupForm(f => ({ ...f, pickup_address: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Items Collected</Label>
                    <Textarea
                      className="text-sm bg-white resize-none"
                      rows={2}
                      placeholder="e.g. 2 shirts, 1 suit jacket, 3 trousers"
                      value={pickupForm.items_collected}
                      onChange={(e) => setPickupForm(f => ({ ...f, items_collected: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              <ProofCapture
                imageData={proofImage}
                onImageCapture={setProofImage}
                onClear={() => setProofImage(null)}
              />
              <Button
                onClick={handleAction}
                disabled={submitting}
                className={`w-full h-13 text-base font-semibold rounded-xl text-white ${COLOR_CLASSES[action.color]}`}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Updating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    {action.label}
                  </span>
                )}
              </Button>
            </div>
          )}

          {isCompleted && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-slate-500">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              Completed
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main dashboard ───────────────────────────────────────────────────────────
// Detect Android so we can show the right "doesn't ring on lock screen"
// guidance — the fix is OS-level (battery optimization / autostart on
// Xiaomi/Oppo/Vivo) and we can't trigger it from JS, only explain it.
const isAndroid = () => {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
};

const LockScreenTipsBanner = ({ subscribed, iosNeedsInstall }) => {
  // Only show once subscribed and dismissable per device.
  const STORAGE_KEY = "pokus_rider_lock_tips_dismissed";
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });
  if (!subscribed || dismissed || iosNeedsInstall) return null;

  const android = isAndroid();
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-xs text-amber-900 space-y-2">
      <div className="flex items-start gap-2">
        <Bell className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-bold">Not hearing alerts when phone is locked?</p>
          {android ? (
            <ul className="mt-1 space-y-0.5 list-disc list-inside text-amber-800">
              <li>Settings → Apps → Chrome → Battery → <b>Unrestricted</b></li>
              <li>On Xiaomi/Oppo/Vivo/Realme: also enable <b>Autostart</b> for Chrome</li>
              <li>Disable Battery Saver / Power Save mode</li>
              <li>Make sure phone isn't on Silent / Do Not Disturb</li>
            </ul>
          ) : (
            <ul className="mt-1 space-y-0.5 list-disc list-inside text-amber-800">
              <li>Settings → Notifications → Pokus → Allow + Sounds + Lock Screen</li>
              <li>Turn off <b>Focus</b> / Do Not Disturb (or allow Pokus inside it)</li>
              <li>Turn off <b>Low Power Mode</b> while on shift</li>
            </ul>
          )}
        </div>
        <button
          onClick={() => {
            try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
            setDismissed(true);
          }}
          className="p-1 -m-1 text-amber-600 active:text-amber-800"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const NotificationBanner = ({ push }) => {
  const { supported, permission, subscribed, busy, enable, iosNeedsInstall } = push;

  // iOS: Web Push only works after the site is added to the Home Screen and
  // launched as a PWA. This check runs BEFORE the generic `!supported` branch
  // because on older iOS `serviceWorker` is undefined in a Safari tab and we
  // want to show actionable install instructions instead of a dead-end message.
  if (iosNeedsInstall) {
    return (
      <div className="bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-xl px-4 py-3.5 space-y-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#0047FF] rounded-xl flex items-center justify-center shrink-0">
            <Bell className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#0047FF]">Install Pokus to get notifications</p>
            <p className="text-xs text-slate-600 leading-tight">
              iPhone only sends push alerts when Pokus is added to your Home Screen.
            </p>
          </div>
        </div>
        <ol className="text-xs text-slate-700 space-y-1.5 pl-1">
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-[#0047FF]/10 text-[#0047FF] flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
            <span className="flex items-center gap-1.5">
              Tap the <Share className="w-3.5 h-3.5 inline text-[#0047FF]" /> Share button in Safari
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-[#0047FF]/10 text-[#0047FF] flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
            <span className="flex items-center gap-1.5">
              Choose <Plus className="w-3.5 h-3.5 inline" /> "Add to Home Screen"
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-[#0047FF]/10 text-[#0047FF] flex items-center justify-center text-[10px] font-bold shrink-0">3</span>
            <span>Open Pokus from the Home Screen icon, then turn on notifications</span>
          </li>
        </ol>
        <p className="text-[10px] text-slate-500 pt-0.5">Requires iOS 16.4 or newer.</p>
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="bg-slate-100 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-600 flex items-start gap-2">
        <BellOff className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <span>This browser doesn't support push notifications. Try Chrome, Safari (iOS 16.4+ installed as PWA), or Firefox.</span>
      </div>
    );
  }
  if (subscribed) return null;
  if (permission === "denied") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
        <BellOff className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <span>Notifications are blocked. Open your browser's site settings and allow notifications to get pinged on new tasks.</span>
      </div>
    );
  }
  return (
    <button
      onClick={async () => {
        try {
          await enable();
          toast.success("Notifications enabled!");
        } catch (e) {
          toast.error(e.message || "Couldn't enable notifications");
        }
      }}
      disabled={busy}
      className="w-full bg-[#0047FF]/5 border border-[#0047FF]/20 rounded-xl px-3 py-3 flex items-center gap-3 text-left active:bg-[#0047FF]/10 transition-colors"
    >
      <div className="w-9 h-9 bg-[#0047FF] rounded-xl flex items-center justify-center shrink-0">
        <Bell className="w-4.5 h-4.5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0047FF]">Turn on notifications</p>
        <p className="text-xs text-slate-600 leading-tight">
          Get pinged the moment you're assigned a pickup or delivery.
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-[#0047FF] shrink-0" />
    </button>
  );
};

export const RiderDashboard = () => {
  const [rider, setRider] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const push = useRiderPush(rider?.id);
  const alarm = useRiderAlarm();

  useEffect(() => {
    const stored = localStorage.getItem("pokus_rider");
    if (!stored) { navigate("/rider"); return; }
    setRider(JSON.parse(stored));
  }, [navigate]);

  const fetchTasks = useCallback(async (quiet = false) => {
    if (!rider) return;
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await fetch(`${API}/riders/${rider.id}/tasks`);
      if (!res.ok) throw new Error();
      setTasks(await res.json());
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [rider]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    if (!rider) return;
    const id = setInterval(() => fetchTasks(true), 20_000);
    return () => clearInterval(id);
  }, [rider, fetchTasks]);

  // Live refresh tasks when a push arrives while the tab is open, and blare
  // the in-app siren for assignments so the rider notices even if their phone
  // is on silent (system push sounds follow device settings; this one doesn't).
  useEffect(() => {
    if (!rider || !("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data && event.data.type === "rider-push") {
        fetchTasks(true);
        const p = event.data.payload || {};
        if (p.title) {
          toast(p.title, { description: p.body });
        }
        const type = p.data && p.data.type;
        if (type === "assignment") {
          alarm.play({ durationMs: 10000 });
        } else if (type === "invoice_linked" || type === "signed" || type === "payout") {
          alarm.play({ durationMs: 3500 });
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [rider, fetchTasks, alarm]);

  const handleLogout = async () => {
    // Tear down the push subscription on this device so the next rider who
    // logs in here doesn't keep receiving notifications meant for the
    // previous rider, and so the backend forgets the endpoint mapping.
    try { await push.disable(); } catch (e) { /* best-effort */ }
    localStorage.removeItem("pokus_rider");
    navigate("/rider");
  };

  const isTaskDone = (t) =>
    t.task_type === "delivered" ||
    (t.task_type === "awaiting_signature" && t.order_signature_status === "signed") ||
    (t.task_type === "customer_delivery" && (
      t.status === "closed" ||
      (t.status === "delivered" && !!t.delivered_items)
    )) ||
    (t.task_type === "c2c_delivery" && t.status === "delivered");

  const activeTasks = tasks.filter((t) => !isTaskDone(t));
  const doneTasks = tasks.filter((t) => isTaskDone(t));

  if (!rider) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3.5 max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0047FF] rounded-xl flex items-center justify-center">
              <Bike className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">{rider.full_name}</p>
              <p className="text-xs text-slate-500">{rider.phone}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (alarm.playing) {
                  alarm.stop();
                  return;
                }
                alarm.toggle();
                toast.success(alarm.enabled ? "Alarm sound off" : "Alarm sound on");
              }}
              title={
                alarm.playing
                  ? "Stop siren"
                  : alarm.enabled
                  ? "Turn off alarm sound"
                  : "Turn on alarm sound"
              }
              className={`p-2 rounded-xl active:bg-slate-100 transition-colors ${
                alarm.playing
                  ? "text-red-500 animate-pulse"
                  : alarm.enabled
                  ? "text-[#0047FF]"
                  : "text-slate-400"
              }`}
            >
              {alarm.enabled ? <Volume2 className="w-4.5 h-4.5" /> : <VolumeX className="w-4.5 h-4.5" />}
            </button>
            {push.supported && !push.iosNeedsInstall && (
              <button
                onClick={async () => {
                  try {
                    if (push.subscribed) {
                      await push.disable();
                      toast.success("Notifications turned off");
                    } else {
                      await push.enable();
                      toast.success("Notifications enabled!");
                    }
                  } catch (e) {
                    toast.error(e.message || "Couldn't update notifications");
                  }
                }}
                disabled={push.busy}
                title={push.subscribed ? "Turn off notifications" : "Turn on notifications"}
                className={`p-2 rounded-xl active:bg-slate-100 transition-colors ${
                  push.subscribed ? "text-[#0047FF]" : "text-slate-500"
                }`}
              >
                {push.subscribed ? <Bell className="w-4.5 h-4.5" /> : <BellOff className="w-4.5 h-4.5" />}
              </button>
            )}
            <button
              onClick={() => navigate("/rider/trips")}
              title="View trip logs"
              className="p-2 rounded-xl text-slate-500 active:bg-slate-100 transition-colors"
            >
              <ScrollText className="w-4.5 h-4.5" />
            </button>
            <button
              onClick={() => fetchTasks(true)}
              disabled={refreshing}
              className="p-2 rounded-xl text-slate-500 active:bg-slate-100 transition-colors"
            >
              <RefreshCw className={`w-4.5 h-4.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-xl text-slate-500 active:bg-slate-100 transition-colors"
            >
              <LogOut className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        <NotificationBanner push={push} />
        <LockScreenTipsBanner
          subscribed={push.subscribed}
          iosNeedsInstall={push.iosNeedsInstall}
        />
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
            <span className="w-8 h-8 border-2 border-slate-300 border-t-[#0047FF] rounded-full animate-spin" />
            <p className="text-sm">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-base font-semibold text-slate-700">All clear!</p>
            <p className="text-sm text-slate-400 text-center">No active tasks right now.</p>
            <button
              onClick={() => fetchTasks(true)}
              className="mt-2 text-sm text-[#0047FF] font-medium flex items-center gap-1"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        ) : (
          <>
            {/* Active tasks */}
            {activeTasks.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest px-1">
                  Active ({activeTasks.length})
                </p>
                {activeTasks.map((task) => (
                  <TaskCard
                    key={`${task.id}-${task.task_type}`}
                    task={task}
                    riderId={rider.id}
                    riderName={rider.full_name}
                    onRefresh={() => fetchTasks(true)}
                  />
                ))}
              </div>
            )}

            {/* Completed today */}
            {doneTasks.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5" /> Awaiting Ops ({doneTasks.length})
                </p>
                {doneTasks.map((task) => (
                  <TaskCard
                    key={`${task.id}-${task.task_type}`}
                    task={task}
                    riderId={rider.id}
                    riderName={rider.full_name}
                    onRefresh={() => fetchTasks(true)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
